import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getUsersWithPreferences,
} from '../../../../api/General/NotificationPreference';
import type { UserNotificationSummaryView } from '../../../../api/General/NotificationPreference';
import { getDepartments, getBranches } from '../../../../api/General/Company';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../../components/ui/Table';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';
import Pagination from '@/components/Pagination';
import BulkPreferencesModal from './BulkPreferencesModal';
import UserPreferencesPanel from './UserPreferencesPanel';

const ROLE_OPTIONS = ['Clocking', 'Hr', 'Manager', 'Chief', 'Admin'];
const PAGE_SIZE = 10;

const UserManagementTab: React.FC = () => {
  const { t } = useTranslation();
  const companyId = useSelector((state: { user?: { company?: { id?: number } } }) => state.user?.company?.id);

  // Filter state
  const [departmentId, setDepartmentId] = useState<number | undefined>();
  const [branchId, setBranchId] = useState<number | undefined>();
  const [role, setRole] = useState<string | undefined>();
  const [search, setSearch] = useState('');

  // Data
  const [users, setUsers] = useState<UserNotificationSummaryView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);

  // Lookups
  const [departments, setDepartments] = useState<{ value: number; label: string }[]>([]);
  const [branches, setBranches] = useState<{ value: number; label: string }[]>([]);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modals
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState('');

  // Fetch lookups
  useEffect(() => {
    if (!companyId) return;
    const loadLookups = async () => {
      try {
        const [deptResult, branchResult] = await Promise.all([
          getDepartments(companyId),
          getBranches(companyId),
        ]);
        if (!deptResult.errors?.length && deptResult.payload) {
          setDepartments(
            deptResult.payload.map((d) => ({ value: d.id, label: d.name }))
          );
        }
        if (!branchResult.errors?.length && branchResult.payload) {
          setBranches(
            branchResult.payload.map((b) => ({ value: b.id, label: b.name }))
          );
        }
      } catch (err) {
        console.error('Failed to load lookups:', err);
      }
    };
    loadLookups();
  }, [companyId]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getUsersWithPreferences({
        departmentId,
        branchId,
        role,
        search: search.trim() || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      if (result.errors?.length) {
        setError(result.errors[0]?.description || t('Failed to load users'));
        return;
      }
      setUsers(result.payload?.users ?? []);
      setTotalCount(result.payload?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(t('Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [departmentId, branchId, role, search, page, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [departmentId, branchId, role, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const currentFilter = useMemo(
    () => ({
      departmentId,
      branchId,
      role,
      search: search.trim() || undefined,
    }),
    [departmentId, branchId, role, search]
  );

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === users.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users.map((u) => u.userId)));
    }
  };

  const toggleSelectUser = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleRowClick = (user: UserNotificationSummaryView) => {
    setSelectedUserId(user.userId);
    setSelectedUserName(user.fullName);
  };

  const roleSelectOptions = useMemo(
    () => [
      { value: '', label: t('All Roles') },
      ...ROLE_OPTIONS.map((r) => ({ value: r, label: t(r) })),
    ],
    [t]
  );

  const departmentSelectOptions = useMemo(
    () => [
      { value: '', label: t('All Departments') },
      ...departments.map((d) => ({ value: String(d.value), label: d.label })),
    ],
    [departments, t]
  );

  const branchSelectOptions = useMemo(
    () => [
      { value: '', label: t('All Branches') },
      ...branches.map((b) => ({ value: String(b.value), label: b.label })),
    ],
    [branches, t]
  );

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <Select
            placeholder={t('All Departments')}
            options={departmentSelectOptions}
            value={departmentId ? String(departmentId) : ''}
            onChange={(val: string) => setDepartmentId(val ? Number(val) : undefined)}
            className="min-w-[180px]"
            clearable
            searchable
          />
          <Select
            placeholder={t('All Branches')}
            options={branchSelectOptions}
            value={branchId ? String(branchId) : ''}
            onChange={(val: string) => setBranchId(val ? Number(val) : undefined)}
            className="min-w-[180px]"
            clearable
            searchable
          />
          <Select
            placeholder={t('All Roles')}
            options={roleSelectOptions}
            value={role || ''}
            onChange={(val: string) => setRole(val || undefined)}
            className="min-w-[160px]"
            clearable
          />
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder={t('Search by name or email...')}
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">
            {t('{{count}} user(s) selected', { count: selectedIds.size })}
          </span>
          <Button
            size="sm"
            onClick={() => setBulkModalOpen(true)}
          >
            <AppIcon name="Settings" size={14} className="ltr:mr-1.5 rtl:ml-1.5" />
            {t('Apply to Selected Users')}
          </Button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive flex items-center gap-2">
          <AppIcon name="AlertCircle" size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={users.length > 0 && selectedIds.size === users.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border border-input bg-background text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                />
              </TableHead>
              <TableHead>{t('Name')}</TableHead>
              <TableHead>{t('Email')}</TableHead>
              <TableHead>{t('Department')}</TableHead>
              <TableHead>{t('Role')}</TableHead>
              <TableHead>{t('Overrides')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <div className="flex items-center gap-2 py-4 justify-center">
                    <Spinner size="sm" />
                    <span className="text-sm text-muted-foreground">
                      {t('Loading...')}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <div className="py-8 text-center">
                    <AppIcon
                      name="Users"
                      size={32}
                      className="mx-auto mb-2 text-muted-foreground/50"
                    />
                    <p className="text-sm text-muted-foreground">
                      {t('No users found')}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.userId}
                  className="cursor-pointer"
                  onClick={() => handleRowClick(user)}
                >
                  <TableCell
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(user.userId)}
                      onChange={() => toggleSelectUser(user.userId)}
                      className="h-4 w-4 rounded border border-input bg-background text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                    />
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-foreground">
                      {user.fullName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">
                      {user.email || '\u2014'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">
                      {user.department || '\u2014'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">
                      {user.role || '\u2014'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {user.customOverrideCount > 0 ? (
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                        {t('{{count}} custom override(s)', {
                          count: user.customOverrideCount,
                        })}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {t('Using defaults')}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <div className="px-1">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(p) => setPage(p)}
            variant="compact"
          />
        </div>
      )}

      {/* Bulk Preferences Modal */}
      <BulkPreferencesModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        selectedCount={selectedIds.size}
        filter={currentFilter}
        onApplied={() => {
          setBulkModalOpen(false);
          setSelectedIds(new Set());
          fetchUsers();
        }}
      />

      {/* User Preferences Panel */}
      {selectedUserId && (
        <UserPreferencesPanel
          open={!!selectedUserId}
          onClose={() => setSelectedUserId(null)}
          userId={selectedUserId}
          userName={selectedUserName}
          onSaved={() => {
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

export default UserManagementTab;
